/**
 * The gate-list snapshot — `device-cache.md`'s *"Last gate-list snapshot"* row, and the
 * status row nested inside it.
 *
 * Three rules from that row are implemented here rather than asserted:
 *
 * 1. **Allow-list, never a spread.** `toCachedGate()` builds each field by hand. A manager's
 *    `GET /gates` response carries `lat`/`lng`/`placeId` and a `subscription` block; the row
 *    says coordinates *"are manager-only and are not needed on Home, so do not include
 *    them"*, and a `{...gate}` here would put them on disk the first time a manager signed
 *    in. Adding a field to the API must not be able to add a field to the cache.
 *
 *    **C2's one deliberate, narrow amendment:** `subscriptionRenewsAt` — the renewal **date**
 *    only, never the whole `subscription` block. The plan/status stay excluded (billing detail
 *    Home has no use for); the date alone is what lets Home's tile answer "is this gate's
 *    subscription running low" without a round trip, and unlike a precomputed day count, a date
 *    does not go stale sitting on disk between launches — `renewalDaysRemaining()` derives the
 *    count at render, same as everywhere else it is read.
 * 2. **Partition by authenticated user and deployment stage.** The key *is* the partition —
 *    `snapshotKey()` is the only way one is built, so "never show another account's
 *    snapshot" (startup contract step 2) is a lookup that misses rather than a check that
 *    can be forgotten.
 * 3. **Never restore a green LED as fresh.** A restored snapshot's statuses are `stale` by
 *    construction (`statusPhaseOf`), and only a response from this launch can make one
 *    `fresh`. §13 "The gate card carries no connectivity LED (D141)"'s ~2 s truthful reading is P7's criterion and is only
 *    reachable because this paints stale first. **D243 made that reading a green wifi glyph rather than an LED**, and
 *    narrowed it to the green half — an unreachable gate now renders unmarked — which changes what Gate Detail paints and
 *    nothing here.
 *
 * The **offline BLE receipt outbox** row of the same document is deliberately absent: D94
 * sequences it, and every rule about it, into **P16**. In the MVP no receipt is ever queued,
 * so an outbox here would be an untested store holding an audit record nothing produces.
 */

import { isGatePosition, type GatePosition } from '@selogate/core'
import { SCHEMA_VERSION, type KeyValueStore } from './store'

/**
 * The four phases {@link statusPhaseOf} can answer with.
 *
 * **It lives here since D243, and the move is what deleting the LED cost.** The type was declared
 * beside `StatusDot`, the component that painted it; D243 removed the dot from the one screen that
 * rendered it, so the component went and the type had to land somewhere that is not a renderer.
 * This is that place: `statusPhaseOf` below is the only thing that produces one, and the type is
 * the shape of its answer rather than the shape of a widget.
 *
 * `stale` is `device-cache.md`'s "never restore a green LED as fresh" rule made a type — a status
 * read back from the snapshot is `stale` until a probe from this launch answers, never straight to
 * `fresh`. **The names outlive the dot deliberately.** They describe evidence, not a colour, which
 * is precisely why they survived a decision that removed every colour they used to select.
 */
export type StatusPhase = 'fresh' | 'checking' | 'stale' | 'offline'

/**
 * What Home renders, and nothing else. Field names follow P3's `GateProjection` so a reader
 * can diff the two; the **absences** are the interesting part (see the header).
 */
export interface CachedGate {
  readonly gateId: string
  readonly defaultName: string
  readonly type: string
  /**
   * The gate's **lifecycle** status (`'active'`), which is P3's `status` field and has
   * nothing to do with the LED. Connectivity is never persisted — see `statusPhaseOf`.
   */
  readonly status: string
  readonly operatingState: string
  /** `location.formatted` only — the display string, never `placeId`/`lat`/`lng` (D51). */
  readonly locationFormatted?: string
  readonly viewerRole: 'manager' | 'resident'
  readonly viewerStatus: 'pending' | 'active' | 'expired'
  /**
   * `lastState.position` only — the gate's last reported position, flattened (D134).
   *
   * The server's `lastState` is a whole `GateStateSnapshot` carrying `firmware`, `alarm`, and
   * (upstream of the parser) the site's SSID and wiring detail. None of that answers "where is
   * the leaf", so none of it is cached; this follows the same allow-list rule as the coordinates.
   * **A cached position is never fresh** — `statusPhaseOf` still governs whether it may be
   * presented as current, exactly as it governs the LED.
   */
  readonly lastPosition?: GatePosition
  readonly lastSeenAt?: string
  /** D89's badge count, contributed to P3's projection by P5. A count, never a roster. */
  readonly unseenExpired?: number
  /**
   * §7.4/§13 "alias, icon, color, drag-order per gate" — the viewer's own alias/icon/color/sortOrder, contributed to P3's projection
   * by P7 (`packages/functions/members/view-contribution.ts`, `implementation-notes.md` → P7).
   * Cosmetic and viewer-scoped, unlike the coordinates/subscription fields this allow-list
   * otherwise excludes — safe to cache at rest.
   */
  readonly alias?: string
  readonly icon?: string
  readonly color?: string
  readonly sortOrder?: number
  /**
   * C2's amendment to the allow-list above — `subscription.renewsAt` only, read for Home's
   * `sub-low` tile rule (C3). Absent for a resident (`GET /gates` sends `subscription` to
   * managers only, D39/§7.10) and for a manager gate with no subscription on record.
   */
  readonly subscriptionRenewsAt?: string
  /**
   * **D153's deliberate amendment to this allow-list.** Manager-only, contributed to P3's
   * projection by `packages/functions/provision/projection.ts`'s `managerCountContribution`
   * (`records.ts`'s `GateItem.managerCount`, D126) — absent for a resident, exactly like
   * `unseenExpired` above. Cached so Gate Detail can seed its Remove-me row correctly on the
   * very first frame of a restored session, before its own roster read resolves, rather than
   * defaulting the row to *present* until that read lands. A manager count is not location PII
   * and not billing detail — the two classes of field this allow-list otherwise excludes — so
   * this is a considered addition, not an opening of the spread this file's header warns against.
   */
  readonly managerCount?: number
  /**
   * **R5 step 2's amendment — the derived BIT, never the object.**
   *
   * P8's `shabbatAutomation` reaches every viewer through P3's one gate projection with **no role
   * gate** (`packages/functions/provision/projection.ts`), and `GET /gates` uses that same
   * projection — so this is already in the list response the snapshot is built from, and its
   * absence here was omission by non-use rather than a decision. It is deliberately *not* in the
   * `toCachedGate` comment's list of fields dropped on purpose.
   *
   * **Carry the bit, never the object.** Days, times, offsets and the locality are what
   * `GET /gates/{id}/shabbat` is for; keeping them at rest would breach the minimalism rule the
   * dropped-field list exists to enforce, for a screen that reads none of them.
   *
   * The derivation is D46 + D174, identical to `readGateDetail`'s: presence *is* on, **except**
   * that D174 suspends rather than deletes, so a present record with `suspended: true` is off.
   *
   * What it buys is not only a faster paint. Gate Detail's mode control gates both
   * `normalSelected` and `permClosedSelected` on `!scheduleActive`; with this field absent from a
   * cache-first render, `scheduleActive` is `false`, **Normal lights up on a gate whose automation
   * is on**, and a tap inside that window skips `suspendShabbatConfig` and latches the gate with
   * the schedule still running — the split the four-pill control exists to prevent.
   */
  readonly shabbatEnabled?: boolean
  /**
   * **R7/D276's amendment to this allow-list — one string, and it is the same argument
   * `shabbatEnabled` made one line up.**
   *
   * It reaches every viewer through P3's one gate projection with **no role gate**, so it is
   * already in the `GET /gates` response this snapshot is built from. What it buys is a correct
   * first paint: Home's large-grid tile draws a Close control only on a `command` gate, and
   * **Home never issues a gate read of its own** (D141) — so without it every `command` gate paints
   * as `auto` and the control appears when the list response lands, or on a cold offline launch
   * never at all. That is D265's cache-first argument reaching a control rather than a spinner.
   *
   * **It is one string and not a block**, which keeps it inside this list's minimalism rule: the
   * mode is a property of the hardware, not location, not billing, and not per-viewer.
   *
   * **Absent, never `'auto'`** — the default resolves in `closeModeOf` alone, and writing the
   * resolved value here would be a second place it lives, which is exactly the drift that rule
   * exists to prevent.
   */
  readonly closeMode?: 'auto' | 'command'
}

export interface GateSnapshot {
  readonly cacheVersion: number
  readonly userId: string
  readonly stage: string
  /** When the server produced it. Purely informational — freshness is not derived from it. */
  readonly fetchedAt: string
  readonly gates: readonly CachedGate[]
}

export function snapshotKey(stage: string, userId: string): string {
  return `${stage}:${userId}`
}

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

/**
 * One gate of a `GET /gates` response, reduced to the cacheable allow-list.
 *
 * Returns `undefined` for anything missing the fields Home cannot render without, so a
 * partial response degrades to fewer cards rather than to a card with holes in it.
 */
export function toCachedGate(raw: unknown): CachedGate | undefined {
  if (!isRecord(raw)) return undefined
  const gateId = str(raw['gateId'])
  const defaultName = str(raw['defaultName'])
  if (gateId === undefined || defaultName === undefined) return undefined

  const viewer = isRecord(raw['viewer']) ? raw['viewer'] : undefined
  const role = viewer?.['role']
  const viewerStatus = viewer?.['status']
  if (role !== 'manager' && role !== 'resident') return undefined
  if (viewerStatus !== 'pending' && viewerStatus !== 'active' && viewerStatus !== 'expired') return undefined

  const location = isRecord(raw['location']) ? raw['location'] : undefined
  const formatted = str(location?.['formatted'])
  // `lastState` is an OBJECT on the wire (P4's `GateStateSnapshot`), not a string. It used to be
  // read here with `str()`, which silently returned `undefined` for every gate ever cached.
  const lastStateRaw = isRecord(raw['lastState']) ? raw['lastState'] : undefined
  const lastPosition = isGatePosition(lastStateRaw?.['position']) ? lastStateRaw['position'] : undefined
  const lastSeenAt = str(raw['lastSeenAt'])
  const unseenExpired = raw['unseenExpired']
  const alias = str(raw['alias'])
  const icon = str(raw['icon'])
  const color = str(raw['color'])
  const sortOrder = raw['sortOrder']
  // C2 — the one field read out of `subscription`. `plan`/`status` are not read at all, so
  // there is no path by which either reaches the cache.
  const subscription = isRecord(raw['subscription']) ? raw['subscription'] : undefined
  const subscriptionRenewsAt = str(subscription?.['renewsAt'])
  // D153 — manager-only, absent for a resident exactly like `unseenExpired`.
  const managerCount = raw['managerCount']
  // R5 step 2 — the derived bit, on the same D46+D174 rule `readGateDetail` applies. Read here
  // rather than stored, so `shabbatAutomation` itself never reaches disk.
  const shabbatAutomation = raw['shabbatAutomation']
  const shabbatEnabled =
    shabbatAutomation === undefined
      ? undefined
      : !(isRecord(shabbatAutomation) && shabbatAutomation['suspended'] === true)
  // R7/D276 — narrowed against the two known modes, so an unrecognised value from a newer server
  // is cached as absent rather than as itself. Absent resolves to `auto` in `closeModeOf`,
  // which is the behaviour every gate already has.
  const closeModeRaw = str(raw['closeMode'])
  const closeMode = closeModeRaw === 'command' || closeModeRaw === 'auto' ? closeModeRaw : undefined

  return {
    gateId,
    defaultName,
    type: str(raw['type']) ?? 'unknown',
    status: str(raw['status']) ?? 'unknown',
    operatingState: str(raw['operatingState']) ?? 'normal',
    // `location.placeId`, `location.lat`, and `location.lng` are read above and deliberately
    // not carried. So are `subscription.plan`/`.status`, `installedByUserId`, `bleConfigured`,
    // and `lastTestResult` — manager-only fields that a manager's own device has no reason to
    // keep at rest for a Home render. `subscription.renewsAt` is the one exception (C2, above),
    // and `shabbatAutomation` is read but reduced to a boolean (R5 step 2) rather than carried.
    ...(formatted === undefined ? {} : { locationFormatted: formatted }),
    viewerRole: role,
    viewerStatus,
    ...(lastPosition === undefined ? {} : { lastPosition }),
    ...(lastSeenAt === undefined ? {} : { lastSeenAt }),
    ...(typeof unseenExpired === 'number' && Number.isFinite(unseenExpired) ? { unseenExpired } : {}),
    ...(alias === undefined ? {} : { alias }),
    ...(icon === undefined ? {} : { icon }),
    ...(color === undefined ? {} : { color }),
    ...(typeof sortOrder === 'number' && Number.isFinite(sortOrder) ? { sortOrder } : {}),
    ...(subscriptionRenewsAt === undefined ? {} : { subscriptionRenewsAt }),
    ...(typeof managerCount === 'number' && Number.isFinite(managerCount) ? { managerCount } : {}),
    // The bit, not `shabbatAutomation` itself — see `CachedGate.shabbatEnabled`. Absent (rather
    // than `false`) when the gate has no record at all, so D135's "an unknown value renders
    // nothing" survives the round trip through the cache.
    ...(shabbatEnabled === undefined ? {} : { shabbatEnabled }),
    // R7/D276 — see `CachedGate.closeMode`. Absent rather than `'auto'`, so the default has
    // exactly one home.
    ...(closeMode === undefined ? {} : { closeMode }),
  }
}

export function buildSnapshot(input: {
  readonly stage: string
  readonly userId: string
  readonly fetchedAt: string
  readonly gates: readonly unknown[]
}): GateSnapshot {
  return {
    cacheVersion: SCHEMA_VERSION,
    stage: input.stage,
    userId: input.userId,
    fetchedAt: input.fetchedAt,
    gates: input.gates.map(toCachedGate).filter((gate): gate is CachedGate => gate !== undefined),
  }
}

/**
 * Read the snapshot belonging to **this** account and stage.
 *
 * A row written by an older schema is discarded rather than migrated: it is derived data,
 * and rebuilding it costs one request (`device-cache.md` → *"Corruption, quota eviction, or
 * schema mismatch"*).
 */
export async function readSnapshot(
  store: KeyValueStore,
  stage: string,
  userId: string,
): Promise<GateSnapshot | undefined> {
  const key = snapshotKey(stage, userId)
  const raw = await store.get<GateSnapshot>('snapshots', key)
  if (raw === undefined) return undefined
  if (raw.cacheVersion !== SCHEMA_VERSION || raw.userId !== userId || raw.stage !== stage) {
    await store.remove('snapshots', key)
    return undefined
  }
  return raw
}

export async function writeSnapshot(store: KeyValueStore, snapshot: GateSnapshot): Promise<void> {
  await store.put('snapshots', snapshotKey(snapshot.stage, snapshot.userId), snapshot)
}

/** Where the gates currently on screen came from. */
export type SnapshotOrigin = 'restored' | 'server'

/** P4's `GateStatusView.liveness`, as it arrives from `POST /gates/{id}/status/refresh`. */
export type Liveness = 'online' | 'offline' | 'unknown'

/**
 * 🔸 **D146's threshold, and the one number in that decision open to veto.** How long a verified
 * reading — *Online* or *Offline* alike — may keep speaking in the present tense. Nothing else in
 * D146 depends on its value, and implementing it does not promote it.
 *
 * **One constant, both verdicts.** The amendment that made the demotion symmetric deliberately
 * added no second threshold: a per-verdict TTL would be a second 🔸 and would need its own reason,
 * and there is none — the claim being bounded is the same claim in both directions.
 */
export const FRESH_READING_TTL_MS = 5 * 60_000

interface StatusPhaseBase {
  /** True while this gate's `POST /gates/{id}/status/refresh` is in flight (§6). */
  readonly probing: boolean
  /** False when the cloud is unreachable — nothing can be verified. */
  readonly online: boolean
  /** The instant to judge the reading's age against. Defaults to `Date.now()`; tests pin it. */
  readonly now?: number
}

/**
 * **A verdict and its age are one value, expressed as a union rather than as two optional
 * fields.** D146 is only true if every producer stamps its reading, and a `probedAt?: number`
 * beside a `probed?: Liveness` would let a caller supply the verdict alone — which is a reading
 * that cannot expire, i.e. exactly the pre-D146 behavior, reachable by omission and green in CI.
 *
 * `probed: undefined` means nothing has verified this gate since the app started, which is the
 * state every restored card begins in.
 */
export type StatusPhaseInput =
  | (StatusPhaseBase & { readonly probed?: undefined; readonly probedAt?: undefined })
  | (StatusPhaseBase & { readonly probed: Liveness; readonly probedAt: number })

/**
 * The LED phase for one gate — **"never restore a green LED as fresh" as a type, not a rule
 * anyone has to remember.**
 *
 * `fresh` is reachable from exactly one combination: online, no probe outstanding, and a
 * probe **from this launch** that answered **within {@link FRESH_READING_TTL_MS}**. Persisted
 * connectivity cannot produce it because no connectivity value is persisted; a restored card is
 * `stale` ("לא נבדק") until §6's round trip returns, which is the whole of the rule.
 *
 * Losing reception also maps to `stale`, never to `offline` ("Offline"): the red LED is a
 * statement about the *gate*, and showing it because the *phone* has no signal is the same
 * false LED in the other direction. §6's red comes from a probe that timed out.
 *
 * **D146 bounds how long an answer speaks in the present tense.** D134/D135 policed which
 * *outcomes* may move a gate's status; neither bounded *for how long*, because the launch-local
 * freshness rule silently assumed probes recur — and D142 removed the recurrence. Post-D142 the
 * only routine probe is Gate Detail's mount, so leaving that screen open across an hour's
 * backgrounding left a green dot resting on an hour-old probe.
 *
 * Three things the demotion is not. **It is not an outcome** — D135's rule that only `answered`
 * and `timeout` may move a gate's status is untouched, and nothing here writes one. **It is not a
 * probe** — no request is issued at any point, at launch, on resume, or at the boundary (D142).
 * **It is not red** — an expired reading lands on `stale`/*לא נבדק*, the label D138 renamed for
 * exactly this claim, never on `offline`. The way back to a verified reading is the app bar's
 * refresh control, which runs `runRefreshProbeCycle` because a user asked it to.
 *
 * **Both verdicts expire, on the one bound (D146, amended the day it was minted).** The expiry
 * comparison sits *above* the verdict switch rather than inside its `online` arm, which is the
 * whole of the amendment: the rule underneath is how long **any** outcome speaks in the present
 * tense, and that is verdict-independent. The original asymmetry was inherited rather than chosen —
 * D146 was raised from the green symptom and framed around it. **A red dot is not the safe
 * direction merely because it under-promises:** it tells a resident their gate is unreachable
 * *now*, on evidence that no longer supports a claim about now, and a resident who believes it may
 * drive somewhere else. Both halves land on the same `stale`/*לא נבדק*, on the same
 * {@link FRESH_READING_TTL_MS} — one constant, never a second threshold to veto.
 */
export function statusPhaseOf(input: StatusPhaseInput): StatusPhase {
  if (input.probing) return 'checking'
  if (!input.online || input.probed === undefined) return 'stale'
  // Above the switch on purpose. Inside the `online` arm — where it began — the same expired
  // reading was honest in one direction and present-tense in the other.
  if (isExpired(input.probedAt, input.now)) return 'stale'
  if (input.probed === 'online') return 'fresh'
  if (input.probed === 'offline') return 'offline'
  return 'stale'
}

/** D146's bound, as a comparison rather than as a schedule — nothing here is asked of the gate. */
export function isExpired(probedAt: number, now = Date.now()): boolean {
  return now - probedAt >= FRESH_READING_TTL_MS
}

/**
 * Startup contract step 7 — *"probe each server-reported stale or unknown status according
 * to PRD §6."* Nothing in a snapshot is ever "not stale" on restore, so on a cold launch
 * that is every gate; a later reconnect re-probes only what no longer has a live answer.
 *
 * **D146 makes "a live answer" time-bounded here too, and it has to.** The decision names the app
 * bar's refresh control as *the* user-initiated way back from a demoted reading — "a build in
 * which that control performs the list read alone fails this criterion". A demotion changes the
 * phase and not the verdict, so a gate whose reading has expired still reads `online` in this map;
 * skipping it would leave the demoted gate with no way back at all, which is a downgrade wearing
 * honesty.
 *
 * `probedAt` defaults to empty rather than to "assume fresh": an unstamped verdict is a reading of
 * unknown age, and the safe reading of unknown age is *ask again*.
 *
 * **The amendment that expires an `offline` verdict too needed nothing here — and the reason is the
 * early return below, not the age comparison.** `probed.get(id) !== 'online'` answers *before*
 * `probedAt` and `now` are read, so for an offline verdict those arguments are unreachable and an
 * offline gate is eligible by the same branch a never-probed gate takes: **`offline` and `undefined`
 * are one case here, deliberately.** A demoted red reading therefore has the same way back a demoted
 * green one has, for free. Two consequences worth stating, because both have a way of being
 * rediscovered as bugs. An assertion that an *expired* offline reading is probe-eligible is
 * **vacuous** — it passes with the age logic deleted, while reading as coverage of D146; the test
 * beside this function pins the *unexpired* case instead, and pins the equivalence itself. And do
 * not "tidy" the early return into a symmetric age test around every verdict — that would spare an
 * unexpired offline gate the probe it gets today, which is a behavior change wearing a refactor.
 */
export function gatesNeedingProbe(
  snapshot: GateSnapshot,
  probed: ReadonlyMap<string, Liveness>,
  probedAt: ReadonlyMap<string, number> = new Map(),
  now: number = Date.now(),
): readonly CachedGate[] {
  return snapshot.gates.filter((gate) => {
    if (probed.get(gate.gateId) !== 'online') return true
    const at = probedAt.get(gate.gateId)
    return at === undefined || isExpired(at, now)
  })
}
